import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-suppliers';
import { header } from '../page/api';
import type { CatalogFilters, CatalogQuery } from './list-state';

export type SupplierRow = components['schemas']['SupplierRow'];
export type MapSite = components['schemas']['MapSite'];

/** A problem the API answered: its `code`, and `message`, the person's words when it has them. */
export interface Problem {
  readonly status: number;
  readonly code: string | null;
  readonly message: string | null;
}

function problemOf(status: number, error: unknown): Problem {
  const body = (typeof error === 'object' && error !== null ? error : {}) as {
    code?: unknown;
    message?: unknown;
  };
  return {
    status,
    code: typeof body.code === 'string' ? body.code : null,
    message: typeof body.message === 'string' ? body.message : null,
  };
}

export type Loaded<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly problem: Problem }
  | ({ readonly status: 'ready' } & T);

export interface CatalogPage {
  readonly items: readonly SupplierRow[];
  readonly total: number;
}

export interface CatalogSites {
  readonly items: readonly MapSite[];
  readonly truncated: boolean;
  readonly unmapped: number;
}

/** One page of «Fornitori» (GET /v1/expenses/suppliers/catalog). */
export async function listSuppliers(query: CatalogQuery): Promise<Loaded<CatalogPage>> {
  try {
    const { data, error, response } = await apiClient.GET('/v1/expenses/suppliers/catalog', {
      params: { header: header(), query },
    });
    if (response.ok && data !== undefined) {
      return { status: 'ready', items: data.items, total: data.total };
    }
    return { status: 'error', problem: problemOf(response.status, error) };
  } catch {
    return { status: 'error', problem: problemOf(0, null) };
  }
}

/** The filtered suppliers' sites with coordinates, for «Mappa» (GET …/catalog/sites). */
export async function listSupplierSites(filters: CatalogFilters): Promise<Loaded<CatalogSites>> {
  try {
    const { data, error, response } = await apiClient.GET('/v1/expenses/suppliers/catalog/sites', {
      params: { header: header(), query: filters },
    });
    if (response.ok && data !== undefined) {
      return {
        status: 'ready',
        items: data.items,
        truncated: data.truncated,
        unmapped: data.unmapped,
      };
    }
    return { status: 'error', problem: problemOf(response.status, error) };
  } catch {
    return { status: 'error', problem: problemOf(0, null) };
  }
}

/**
 * The CSV of the filtered list (GET …/catalog/export): every row, or only the page shown when
 * `page` is given. Throws an error carrying the problem's `code` (EXPORT_ROW_LIMIT_EXCEEDED…).
 */
export async function exportSuppliers(
  query: CatalogQuery,
  page: boolean,
  fileName: string,
): Promise<void> {
  const { offset, limit, ...filters } = query;
  const { data, error, response } = await apiClient.GET('/v1/expenses/suppliers/catalog/export', {
    params: {
      header: header(),
      query: { ...filters, format: 'csv', ...(page ? { offset, limit } : {}) },
    },
    parseAs: 'blob',
  });
  if (!response.ok || data === undefined) {
    const problem = problemOf(response.status, error);
    throw Object.assign(new Error('Export failed'), { code: problem.code ?? '' });
  }
  const url = URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * A read that follows its key: a new key shows «loading» and drops a late answer for the old one.
 * `reload` asks again for the same key.
 */
export function useLoaded<T>(
  key: string,
  load: () => Promise<Loaded<T>>,
): { readonly state: Loaded<T>; readonly reload: () => void } {
  const [answer, setAnswer] = useState<{ key: string; state: Loaded<T> } | null>(null);
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    void load().then((state) => {
      if (live) setAnswer({ key, state });
    });
    return () => {
      live = false;
    };
    // `load` is rebuilt on every render; the key says when it asks for something else.
  }, [key, round]);
  const reload = useCallback(() => {
    setAnswer(null);
    setRound((n) => n + 1);
  }, []);
  const state: Loaded<T> =
    answer !== null && answer.key === key ? answer.state : { status: 'loading' };
  return { state, reload };
}
